import { Task } from "@/types/flows";
import { db } from "@/firebase/app";
import { doc, onSnapshot, DocumentSnapshot, getDoc } from "firebase/firestore";
import { RapidStateCreator } from "../types";
import { ExampleData } from "./workflowSlice";
import { deleteRapidData } from "@utils/api/rapiddata";
import type { ModelChange } from "@/components/flow/deploy/Steps/Model/types";
import { CIEvaluationResult } from "@functions/continuousImprovement/continuousImprovementTypes";

export type Visibility = "public" | "private";

export interface RapidModelsSavedInfo {
    // TODO: Define the structure of saved models info
    [key: string]: any;
}

export interface DatasetInfo {
    id: string;
    name: string;
    url: string;
    icon: string | null;
    iconOwner: string | null;
    iconHasAnnotation: boolean;
    annotation: string | null;
    envDomain: string;
    createdAt?: Date | string;
}

export interface RapidDataType {
    id: string;
    task: NonNullable<Task>;
    workspaceId: string;
    workflowId: string;
    models: RapidModelsSavedInfo;
    lastExecutedAt?: Date;
    exampleData: ExampleData;
    workflowName?: string;
    workflowUrl?: string;
    datasets: DatasetInfo[];
    lastCIEvaluation?: { result: CIEvaluationResult; timestamp?: Date };
    modelChanges?: ModelChange[];
}

export interface RapidDataSliceState {
    rapidDataRecords: RapidDataType[];
    currentRapidData: RapidDataType | null;
    currentRapidDataLoading: boolean;
    currentRapidDataError: string | null;

    // Workspace models state
    workspaceModels: RapidDataType[];
    workspaceModelsLoading: boolean;
    workspaceModelsError: string | null;

    // Getters
    getCurrentRapidData: () => RapidDataType | null;
    getRapidDataById: (id: string) => RapidDataType | undefined;
    getRapidDataByWorkflowId: (workflowId: string) => RapidDataType | undefined;
    getRapidDataByWorkspaceId: (workspaceId: string) => RapidDataType[];

    // Setters
    setRapidDataRecords: (records: RapidDataType[]) => void;
    setCurrentRapidData: (rapidData: RapidDataType | null) => void;
    setCurrentRapidDataLoading: (currentRapidDataLoading: boolean) => void;
    setCurrentRapidDataError: (currentRapidDataError: string | null) => void;

    // Actions
    addRapidData: (rapidData: RapidDataType) => void;
    updateRapidData: (id: string, updates: Partial<RapidDataType>) => void;
    removeRapidData: (id: string) => void;
    clearRapidDataState: () => void;

    // Real-time sync actions
    startListeningToRapidData: (rapidDataId: string) => void;
    stopListeningToRapidData: () => void;
    syncRapidDataFromWorkflow: (workflowRapidDataId: string | null) => void;
    fetchPublicRapidData: (rapidDataId: string, api: any) => Promise<void>;

    // Workspace models actions
    fetchWorkspaceModels: (workspaceId: string, api: any) => Promise<void>;

    // CI alert dismiss state
    lastDismissedCITimestamps: Record<string, number>;
    dismissCIAlert: (rapidDataId: string) => void;

    // Future API Actions (placeholders)
    fetchRapidDataById: (id: string, options?: { bustCache?: boolean }) => Promise<void>;
    fetchRapidDataByWorkflowId: (workflowId: string, api: any) => Promise<void>;
    createRapidData: (rapidData: Omit<RapidDataType, "id">, api: any) => Promise<void>;
    updateRapidDataOnServer: (
        id: string,
        updates: Partial<RapidDataType>,
        api: any
    ) => Promise<void>;
    deleteRapidDataOnServer: (id: string, api: any) => Promise<void>;
}

export const createRapidDataSlice: RapidStateCreator<RapidDataSliceState> = (set, get) => ({
    // Initial state
    rapidDataRecords: [] as RapidDataType[],
    currentRapidData: null as RapidDataType | null,
    currentRapidDataLoading: false,
    currentRapidDataError: null,

    // Workspace models state
    workspaceModels: [] as RapidDataType[],
    workspaceModelsLoading: false,
    workspaceModelsError: null,

    // Private state for real-time sync
    _currentRapidDataUnsubscribe: null as (() => void) | null,
    _currentListeningRapidDataId: null as string | null,

    // CI alert dismiss state
    lastDismissedCITimestamps: {} as Record<string, number>,

    // Getters
    getCurrentRapidData: () => get().currentRapidData,

    getRapidDataById: (id: string) => {
        const records = get().rapidDataRecords;
        return records.find((record: RapidDataType) => record.id === id);
    },

    getRapidDataByWorkflowId: (workflowId: string) => {
        const records = get().rapidDataRecords;
        return records.find((record: RapidDataType) => record.workflowId === workflowId);
    },

    getRapidDataByWorkspaceId: (workspaceId: string) => {
        const records = get().rapidDataRecords;
        return records.filter((record: RapidDataType) => record.workspaceId === workspaceId);
    },

    // Setters
    setRapidDataRecords: (rapidDataRecords: RapidDataType[]) =>
        set({ rapidDataRecords }, false, "setRapidDataRecords"),

    setCurrentRapidData: (currentRapidData: RapidDataType | null) =>
        set({ currentRapidData }, false, "setCurrentRapidData"),

    setCurrentRapidDataLoading: (currentRapidDataLoading: boolean) =>
        set({ currentRapidDataLoading }, false, "setCurrentRapidDataLoading"),

    setCurrentRapidDataError: (currentRapidDataError: string | null) =>
        set({ currentRapidDataError }, false, "setCurrentRapidDataError"),

    // Actions
    addRapidData: (rapidData: RapidDataType) => {
        const currentRecords = get().rapidDataRecords;
        const existingIndex = currentRecords.findIndex(
            (record: RapidDataType) => record.id === rapidData.id
        );

        let newRecords;
        if (existingIndex >= 0) {
            // Update existing record
            newRecords = [...currentRecords];
            newRecords[existingIndex] = rapidData;
        } else {
            // Add new record
            newRecords = [...currentRecords, rapidData];
        }

        set({ rapidDataRecords: newRecords }, false, "addRapidData");
    },

    updateRapidData: (id: string, updates: Partial<RapidDataType>) => {
        const currentRecords = get().rapidDataRecords;
        const recordIndex = currentRecords.findIndex((record: RapidDataType) => record.id === id);

        if (recordIndex >= 0) {
            const newRecords = [...currentRecords];
            newRecords[recordIndex] = { ...newRecords[recordIndex], ...updates };
            set({ rapidDataRecords: newRecords }, false, "updateRapidData");

            // Update current rapid data if it's the one being updated
            const currentRapidData = get().currentRapidData;
            if (currentRapidData && currentRapidData.id === id) {
                set(
                    { currentRapidData: { ...currentRapidData, ...updates } },
                    false,
                    "updateCurrentRapidData"
                );
            }
        }
    },

    removeRapidData: (id: string) => {
        const currentRecords = get().rapidDataRecords;
        const newRecords = currentRecords.filter((record: RapidDataType) => record.id !== id);
        set({ rapidDataRecords: newRecords }, false, "removeRapidData");

        // Clear current rapid data if it's the one being removed
        const currentRapidData = get().currentRapidData;
        if (currentRapidData && currentRapidData.id === id) {
            set({ currentRapidData: null }, false, "clearCurrentRapidData");
        }
    },

    clearRapidDataState: () => {
        // Stop listening to current rapid data
        get().stopListeningToRapidData();

        set(
            {
                rapidDataRecords: [],
                currentRapidData: null,
                currentRapidDataLoading: false,
                currentRapidDataError: null
            },
            false,
            "clearRapidDataState"
        );
    },

    dismissCIAlert: (rapidDataId: string) => {
        set(
            {
                lastDismissedCITimestamps: {
                    ...get().lastDismissedCITimestamps,
                    [rapidDataId]: Date.now()
                }
            },
            false,
            "dismissCIAlert"
        );
    },

    // Real-time sync actions
    startListeningToRapidData: (rapidDataId: string) => {
        if (get()._currentListeningRapidDataId === rapidDataId) {
            return;
        }

        get().stopListeningToRapidData();

        set(
            { currentRapidData: null, currentRapidDataLoading: true, currentRapidDataError: null },
            false,
            "startListeningToRapidData:clearStale"
        );

        try {
            // Start listening to the rapid data document
            const unsubscribe = onSnapshot(
                doc(db, "rapid_data", rapidDataId),
                (snapshot: DocumentSnapshot) => {
                    if (snapshot.exists()) {
                        const rapidData = { ...snapshot.data(), id: snapshot.id } as RapidDataType;
                        set(
                            {
                                currentRapidData: rapidData,
                                currentRapidDataLoading: false,
                                currentRapidDataError: null
                            },
                            false,
                            "startListeningToRapidData:update"
                        );

                        // Also add to records if not already there
                        get().addRapidData(rapidData);
                    } else {
                        set(
                            {
                                currentRapidData: null,
                                currentRapidDataLoading: false,
                                currentRapidDataError: "Rapid data not found"
                            },
                            false,
                            "startListeningToRapidData:notFound"
                        );
                    }
                },
                (error) => {
                    console.error("Error listening to rapid data:", error);
                    set(
                        {
                            currentRapidDataError:
                                error.message || "Failed to listen to rapid data",
                            currentRapidDataLoading: false
                        },
                        false,
                        "startListeningToRapidData:error"
                    );
                }
            );

            // Store the unsubscribe function and current listening ID
            set(
                {
                    _currentRapidDataUnsubscribe: unsubscribe,
                    _currentListeningRapidDataId: rapidDataId,
                    currentRapidDataLoading: true
                },
                false,
                "startListeningToRapidData:start"
            );
        } catch (error) {
            console.error("Error setting up rapid data listener:", error);
            set(
                {
                    currentRapidDataError:
                        error instanceof Error
                            ? error.message
                            : "Failed to set up rapid data listener",
                    currentRapidDataLoading: false
                },
                false,
                "startListeningToRapidData:setupError"
            );
        }
    },

    stopListeningToRapidData: () => {
        const unsubscribe = get()._currentRapidDataUnsubscribe;
        if (unsubscribe) {
            unsubscribe();
            set(
                { _currentRapidDataUnsubscribe: null, _currentListeningRapidDataId: null },
                false,
                "stopListeningToRapidData"
            );
        }
    },

    syncRapidDataFromWorkflow: (workflowRapidDataId: string | null) => {
        if (workflowRapidDataId) {
            get().startListeningToRapidData(workflowRapidDataId);
        } else {
            get().stopListeningToRapidData();
            set({ currentRapidData: null }, false, "syncRapidDataFromWorkflow:clear");
        }
    },

    fetchPublicRapidData: async (rapidDataId: string, api: any) => {
        try {
            const state = get();

            // Prevent duplicate calls if already loading the same data
            if (state.currentRapidDataLoading) {
                return;
            }

            // Skip if we already have this exact data loaded
            if (state.currentRapidData?.id === rapidDataId) {
                return;
            }

            set(
                { currentRapidDataLoading: true, currentRapidDataError: null },
                false,
                "fetchPublicRapidData:start"
            );

            const response = await api.core.getPublicRapidData(rapidDataId);
            const rapidData = (response?.data || response) as RapidDataType;

            if (rapidData) {
                set(
                    {
                        currentRapidData: rapidData,
                        currentRapidDataLoading: false,
                        currentRapidDataError: null
                    },
                    false,
                    "fetchPublicRapidData:success"
                );

                // Also add to records
                get().addRapidData(rapidData);
            } else {
                set(
                    {
                        currentRapidDataError: "RapidData not found",
                        currentRapidDataLoading: false
                    },
                    false,
                    "fetchPublicRapidData:notFound"
                );
            }
        } catch (error) {
            console.error("Error fetching public rapidData:", error);
            const errorMessage =
                error instanceof Error ? error.message : "Failed to fetch rapidData";
            set(
                { currentRapidDataError: errorMessage, currentRapidDataLoading: false },
                false,
                "fetchPublicRapidData:error"
            );
        }
    },

    fetchWorkspaceModels: async (workspaceId: string, api: any) => {
        try {
            set(
                { workspaceModelsLoading: true, workspaceModelsError: null },
                false,
                "fetchWorkspaceModels:start"
            );

            const response = await api.core.getWorkspaceRapidModels(workspaceId);
            const models = (response?.data || response) as RapidDataType[];

            set(
                {
                    workspaceModels: models,
                    workspaceModelsLoading: false,
                    workspaceModelsError: null
                },
                false,
                "fetchWorkspaceModels:success"
            );
        } catch (error) {
            console.error("Error fetching workspace models:", error);
            const errorMessage =
                error instanceof Error ? error.message : "Failed to fetch workspace models";
            set(
                { workspaceModelsError: errorMessage, workspaceModelsLoading: false },
                false,
                "fetchWorkspaceModels:error"
            );
        }
    },

    fetchRapidDataById: async (id: string, options?: { bustCache?: boolean }) => {
        try {
            const currentRapidData = get().getCurrentRapidData();
            if (!options?.bustCache && currentRapidData && currentRapidData.id === id) {
                return;
            }

            set(
                { currentRapidDataLoading: true, currentRapidDataError: null },
                false,
                "fetchRapidDataById:start"
            );

            getDoc(doc(db, "rapid_data", id)).then((docSnapshot) => {
                if (docSnapshot.exists()) {
                    set(
                        {
                            currentRapidData: {
                                ...docSnapshot.data(),
                                id: docSnapshot.id
                            } as RapidDataType,
                            currentRapidDataLoading: false
                        },
                        false,
                        "fetchRapidDataById:success"
                    );
                } else {
                    set(
                        {
                            currentRapidDataError: "Rapid data not found",
                            currentRapidDataLoading: false
                        },
                        false,
                        "fetchRapidDataById:notFound"
                    );
                }
            });

            console.warn("fetchRapidDataById not implemented yet");
            set({ currentRapidDataLoading: false }, false, "fetchRapidDataById:placeholder");
        } catch (error) {
            console.error("Error fetching rapid data by ID:", error);
            const errorMessage =
                error instanceof Error ? error.message : "Failed to fetch rapid data";
            set(
                { currentRapidDataError: errorMessage, currentRapidDataLoading: false },
                false,
                "fetchRapidDataById:error"
            );
        }
    },

    // Future API Actions (placeholder implementations)
    fetchRapidDataByWorkflowId: async (workflowId: string, api: any) => {
        throw new Error("NOT IMPLEMENTED: fetchRapidDataByWorkflowId");
        try {
            set(
                { rapidDataByWorkflowLoading: true, rapidDataByWorkflowError: null },
                false,
                "fetchRapidDataByWorkflowId:start"
            );

            // TODO: Implement API call
            // const rapidDataByWorkflowData = await api.core.getRapidDataByWorkflowId(workflowId);
            // set({ rapidDataByWorkflowData: rapidDataByWorkflowData, rapidDataByWorkflowLoading: false }, false, "fetchRapidDataByWorkflowId:success");

            console.warn("fetchRapidDataByWorkflowId not implemented yet");
            set(
                { rapidDataByWorkflowLoading: false },
                false,
                "fetchRapidDataByWorkflowId:placeholder"
            );
        } catch (error) {
            console.error("Error fetching rapid data by workflow ID:", error);
            const errorMessage =
                error instanceof Error ? error.message : "Failed to fetch rapid data";
            set(
                { rapidDataByWorkflowError: errorMessage, rapidDataByWorkflowLoading: false },
                false,
                "fetchRapidDataByWorkflowId:error"
            );
        }
    },

    createRapidData: async (rapidData: Omit<RapidDataType, "id">, api: any) => {
        throw new Error("NOT IMPLEMENTED: createRapidData");
        try {
            set(
                { createRapidDataLoading: true, createRapidDataError: null },
                false,
                "createRapidData:start"
            );

            // TODO: Implement API call
            // const createRapidDataData = await api.core.getcreateRapidDataId(workflowId);
            // set({ createRapidDataData: createRapidDataData, createRapidDataLoading: false }, false, "createRapidData:success");

            console.warn("createRapidData not implemented yet");
            set({ createRapidDataLoading: false }, false, "createRapidData:placeholder");
        } catch (error) {
            console.error("Error fetching rapid data by workflow ID:", error);
            const errorMessage =
                error instanceof Error ? error.message : "Failed to fetch rapid data";
            set(
                { createRapidDataError: errorMessage, createRapidDataLoading: false },
                false,
                "createRapidData:error"
            );
        }
    },

    updateRapidDataOnServer: async (id: string, updates: Partial<RapidDataType>, api: any) => {
        throw new Error("NOT IMPLEMENTED: updateRapidDataOnServer");
        try {
            set(
                { updateRapidDataOnServerLoading: true, updateRapidDataOnServerError: null },
                false,
                "updateRapidDataOnServer:start"
            );

            // TODO: Implement API call
            // const updateRapidDataOnServerData = await api.core.getupdateRapidDataOnServerId(workflowId);
            // set({ updateRapidDataOnServerData: updateRapidDataOnServerData, updateRapidDataOnServerLoading: false }, false, "updateRapidDataOnServer:success");

            console.warn("updateRapidDataOnServer not implemented yet");
            set(
                { updateRapidDataOnServerLoading: false },
                false,
                "updateRapidDataOnServer:placeholder"
            );
        } catch (error) {
            console.error("Error fetching rapid data by workflow ID:", error);
            const errorMessage =
                error instanceof Error ? error.message : "Failed to fetch rapid data";
            set(
                {
                    updateRapidDataOnServerError: errorMessage,
                    updateRapidDataOnServerLoading: false
                },
                false,
                "updateRapidDataOnServer:error"
            );
        }
    },

    deleteRapidDataOnServer: async (id: string, api: any) => {
        try {
            await deleteRapidData(id);

            get().removeRapidData(id);

            const workspaceModels = get().workspaceModels;
            const updatedWorkspaceModels = workspaceModels.filter((model) => model.id !== id);
            if (updatedWorkspaceModels.length !== workspaceModels.length) {
                set(
                    { workspaceModels: updatedWorkspaceModels },
                    false,
                    "deleteRapidDataOnServer:updateWorkspaceModels"
                );
            }
        } catch (error) {
            console.error("Error deleting rapid data:", error);
            throw error;
        }
    }
});
